/**
 * Invite User Dialog Component
 * Form to invite new users with role selection
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { useInviteUser } from '../../stores/users';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { User } from '../../types/api';

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface InviteResponse {
  message: string;
  user: User;
  inviteUrl: string;
  token: string;
  emailSent: boolean;
}

export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<User['role']>('Viewer');
  const [inviteUrl, setInviteUrl] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');

  const inviteMutation = useInviteUser();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    try {
      const data = await inviteMutation.mutate({
        email,
        name: name || undefined,
        role,
        baseUrl: window.location.origin,
      }) as InviteResponse;
      setInviteUrl(data.inviteUrl);
      setEmailSent(data.emailSent);
      toast.success('User invited successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user');
    }
  }

  function handleClose() {
    setEmail('');
    setName('');
    setRole('Viewer');
    setInviteUrl('');
    setEmailSent(false);
    setError('');
    onOpenChange(false);
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(inviteUrl);
    alert('Invite link copied to clipboard!');
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            Send an invitation to a new user. They'll receive a link to complete their registration.
          </DialogDescription>
        </DialogHeader>

        {!inviteUrl ? (
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={inviteMutation.isPending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name (optional)</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={inviteMutation.isPending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select value={role} onValueChange={(value) => setRole(value as User['role'])}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Editor">Editor</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Viewer">Viewer</SelectItem>
                    <SelectItem value="External">External</SelectItem>
                    <SelectItem value="Photographer">Photographer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={inviteMutation.isPending}>
                {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <div className={`rounded-md p-4 ${emailSent ? 'bg-success-100 border-success-500/20' : 'bg-warning-100 border-warning-500/20'} border`}>
              <p className={`text-sm font-medium mb-2 ${emailSent ? 'text-success-700' : 'text-warning-700'}`}>
                {emailSent ? '✓ User invited successfully!' : '⚠ User invited - Email not sent'}
              </p>
              <p className={`text-xs ${emailSent ? 'text-success-700' : 'text-warning-700'}`}>
                {emailSent
                  ? 'An invitation email has been sent to the user.'
                  : 'Please share this invitation link with the user manually:'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Invitation Link</Label>
              <div className="flex gap-2">
                <Input value={inviteUrl} readOnly className="font-mono text-sm" />
                <Button type="button" onClick={copyToClipboard}>
                  Copy
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                This link will expire in 7 days.
              </p>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
